// ============================================================================
// DRAWING MARKUP LAYER - Manages markup layers and list of markups
// Date: January 2026
// ============================================================================

'use client';

import React, { useState } from 'react';
import {
  Layers, Eye, EyeOff, Trash2, Edit2, Plus, Lock, Unlock,
  MoreVertical, ChevronRight, ChevronDown, Filter,
  ArrowRight, Square, Circle, Type, Cloud, PenTool, Ruler, MessageSquare
} from 'lucide-react';
import type { DrawingMarkup, MarkupType, DrawingMarkupLayer } from './types';

// ============================================================================
// TYPES
// ============================================================================

interface MarkupLayerPanelProps {
  markups: DrawingMarkup[];
  layers: DrawingMarkupLayer[];
  selectedMarkupId: string | null;
  onSelectMarkup: (id: string | null) => void;
  onDeleteMarkup: (id: string) => void;
  onToggleMarkupVisibility: (id: string) => void;
  onToggleLayerVisibility: (layerName: string) => void;
  onCreateLayer: (name: string) => void;
  onDeleteLayer: (layerName: string) => void;
  onMoveMarkupToLayer: (markupId: string, layerName: string) => void;
  currentUserId?: number;
  isOpen: boolean;
  onToggle: () => void;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const MARKUP_TYPE_ICONS: Record<MarkupType, React.ComponentType<{ className?: string }>> = {
  arrow: ArrowRight,
  rectangle: Square,
  circle: Circle,
  line: PenTool,
  freehand: PenTool,
  cloud: Cloud,
  text: Type,
  callout: MessageSquare,
  dimension: Ruler,
  highlight: Square,
  stamp: Square,
  image: Square,
  polyline: PenTool,
  polygon: Square,
  ellipse: Circle,
};

const MARKUP_TYPE_LABELS: Record<MarkupType, string> = {
  arrow: 'Arrow',
  rectangle: 'Rectangle',
  circle: 'Circle',
  line: 'Line',
  freehand: 'Freehand',
  cloud: 'Revision Cloud',
  text: 'Text',
  callout: 'Callout',
  dimension: 'Dimension',
  highlight: 'Highlight',
  stamp: 'Stamp',
  image: 'Image',
  polyline: 'Polyline',
  polygon: 'Polygon',
  ellipse: 'Ellipse',
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

interface MarkupItemProps {
  markup: DrawingMarkup;
  isSelected: boolean;
  onClick: () => void;
  onDelete: () => void;
  onToggleVisibility: () => void;
  canEdit: boolean;
}

function MarkupItem({ markup, isSelected, onClick, onDelete, onToggleVisibility, canEdit }: MarkupItemProps) {
  const [showMenu, setShowMenu] = useState(false);
  const Icon = MARKUP_TYPE_ICONS[markup.markup_type] || Square;
  const label = MARKUP_TYPE_LABELS[markup.markup_type] || markup.markup_type;

  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors ${
        isSelected 
          ? 'bg-blue-500/20 border-l-2 border-blue-500' 
          : 'hover:bg-gray-700/50 border-l-2 border-transparent'
      }`}
      onClick={onClick}
    >
      {/* Color indicator */}
      <div 
        className="w-3 h-3 rounded-full flex-shrink-0" 
        style={{ backgroundColor: markup.color }}
      />
      
      {/* Icon */}
      <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
      
      {/* Label */}
      <div className="flex-1 min-w-0">
        <span className="text-sm text-white truncate block">{label}</span>
        <span className="text-xs text-gray-500 truncate block">
          {markup.user_name} • {new Date(markup.created_at).toLocaleDateString()}
        </span>
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={(e) => { e.stopPropagation(); onToggleVisibility(); }}
          className="p-1 rounded hover:bg-white/10"
          title={markup.is_visible ? 'Hide' : 'Show'}
        >
          {markup.is_visible ? (
            <Eye className="w-3.5 h-3.5 text-gray-400" />
          ) : (
            <EyeOff className="w-3.5 h-3.5 text-gray-500" />
          )}
        </button>
        
        {canEdit && (
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
              className="p-1 rounded hover:bg-white/10"
            >
              <MoreVertical className="w-3.5 h-3.5 text-gray-400" />
            </button>
            
            {showMenu && (
              <div className="absolute right-0 top-full mt-1 w-32 bg-gray-800 rounded-lg shadow-lg border border-gray-700 py-1 z-20">
                <button
                  onClick={(e) => { e.stopPropagation(); /* TODO: Edit */ setShowMenu(false); }}
                  className="w-full px-3 py-1.5 text-left text-sm text-white hover:bg-gray-700 flex items-center gap-2"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                  Edit
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(); setShowMenu(false); }}
                  className="w-full px-3 py-1.5 text-left text-sm text-red-400 hover:bg-gray-700 flex items-center gap-2"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

interface LayerGroupProps {
  layer: DrawingMarkupLayer;
  markups: DrawingMarkup[];
  selectedMarkupId: string | null;
  onSelectMarkup: (id: string | null) => void;
  onDeleteMarkup: (id: string) => void;
  onToggleMarkupVisibility: (id: string) => void;
  onToggleLayerVisibility: () => void;
  onDeleteLayer: () => void;
  currentUserId?: number;
  isDefaultLayer: boolean;
}

function LayerGroup({
  layer,
  markups,
  selectedMarkupId,
  onSelectMarkup,
  onDeleteMarkup,
  onToggleMarkupVisibility,
  onToggleLayerVisibility,
  onDeleteLayer,
  currentUserId,
  isDefaultLayer
}: LayerGroupProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const layerMarkups = markups.filter(m => m.layer_name === layer.name);

  return (
    <div className="border-b border-gray-800">
      {/* Layer header */}
      <div 
        className="flex items-center gap-2 px-3 py-2 bg-gray-800/50 cursor-pointer hover:bg-gray-800"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400" />
        )}
        
        <div 
          className="w-3 h-3 rounded flex-shrink-0" 
          style={{ backgroundColor: layer.color }}
        />
        
        <span className="flex-1 text-sm font-medium text-white">{layer.name}</span>
        
        <span className="text-xs text-gray-500 px-1.5 py-0.5 bg-gray-700 rounded">
          {layerMarkups.length}
        </span>
        
        <button
          onClick={(e) => { e.stopPropagation(); onToggleLayerVisibility(); }}
          className="p-1 rounded hover:bg-white/10"
        >
          {layer.is_visible ? (
            <Eye className="w-3.5 h-3.5 text-gray-400" />
          ) : (
            <EyeOff className="w-3.5 h-3.5 text-gray-500" />
          )}
        </button>
        
        {layer.is_locked && (
          <Lock className="w-3.5 h-3.5 text-gray-500" />
        )}
        
        {!isDefaultLayer && (
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
              className="p-1 rounded hover:bg-white/10"
            >
              <MoreVertical className="w-3.5 h-3.5 text-gray-400" />
            </button>
            
            {showMenu && (
              <div className="absolute right-0 top-full mt-1 w-32 bg-gray-800 rounded-lg shadow-lg border border-gray-700 py-1 z-20">
                <button
                  onClick={(e) => { e.stopPropagation(); onDeleteLayer(); setShowMenu(false); }}
                  className="w-full px-3 py-1.5 text-left text-sm text-red-400 hover:bg-gray-700 flex items-center gap-2"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete Layer
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Markups in layer */}
      {isExpanded && layerMarkups.length > 0 && (
        <div>
          {layerMarkups.map(markup => (
            <MarkupItem
              key={markup.id}
              markup={markup}
              isSelected={selectedMarkupId === markup.id}
              onClick={() => onSelectMarkup(markup.id)}
              onDelete={() => onDeleteMarkup(markup.id)}
              onToggleVisibility={() => onToggleMarkupVisibility(markup.id)}
              canEdit={!layer.is_locked && (!currentUserId || markup.user_id === currentUserId)}
            />
          ))}
        </div>
      )}
      
      {isExpanded && layerMarkups.length === 0 && (
        <div className="px-3 py-4 text-center text-sm text-gray-500">
          No markups in this layer
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function MarkupLayerPanel({
  markups,
  layers,
  selectedMarkupId,
  onSelectMarkup,
  onDeleteMarkup,
  onToggleMarkupVisibility,
  onToggleLayerVisibility,
  onCreateLayer,
  onDeleteLayer,
  onMoveMarkupToLayer,
  currentUserId,
  isOpen,
  onToggle
}: MarkupLayerPanelProps) {
  const [showNewLayerInput, setShowNewLayerInput] = useState(false);
  const [newLayerName, setNewLayerName] = useState('');
  const [filterType, setFilterType] = useState<MarkupType | 'all'>('all');

  // Ensure default layer exists
  const allLayers: DrawingMarkupLayer[] = layers.length > 0 ? layers : [
    { name: 'default', color: '#3b82f6', is_visible: true, is_locked: false, order: 0 }
  ];

  const handleCreateLayer = () => {
    if (newLayerName.trim()) {
      onCreateLayer(newLayerName.trim());
      setNewLayerName('');
      setShowNewLayerInput(false);
    }
  };

  // Filter markups
  const filteredMarkups = filterType === 'all' 
    ? markups 
    : markups.filter(m => m.markup_type === filterType);

  // Get unique markup types for filter
  const markupTypes = [...new Set(markups.map(m => m.markup_type))];

  if (!isOpen) {
    return (
      <button
        onClick={onToggle}
        className="absolute top-4 right-4 p-2 rounded-lg bg-gray-900/90 backdrop-blur text-white hover:bg-gray-800"
        title="Show Layers"
      >
        <Layers className="w-5 h-5" />
      </button>
    );
  }

  return (
    <div className="w-64 bg-gray-900 border-l border-gray-800 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-800">
        <div className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-gray-400" />
          <span className="font-semibold text-white">Markups</span>
          <span className="text-xs text-gray-500 px-1.5 py-0.5 bg-gray-700 rounded">
            {markups.length}
          </span>
        </div>
        <button onClick={onToggle} className="p-1 rounded hover:bg-white/10 text-gray-400">
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Filter */}
      <div className="p-2 border-b border-gray-800">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as MarkupType | 'all')}
            className="flex-1 bg-gray-800 text-white text-sm rounded px-2 py-1 border border-gray-700"
          >
            <option value="all">All Types</option>
            {markupTypes.map(type => (
              <option key={type} value={type}>
                {MARKUP_TYPE_LABELS[type] || type}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Layers */}
      <div className="flex-1 overflow-y-auto">
        {allLayers.map((layer, idx) => (
          <LayerGroup
            key={layer.name}
            layer={layer}
            markups={filteredMarkups}
            selectedMarkupId={selectedMarkupId}
            onSelectMarkup={onSelectMarkup}
            onDeleteMarkup={onDeleteMarkup}
            onToggleMarkupVisibility={onToggleMarkupVisibility}
            onToggleLayerVisibility={() => onToggleLayerVisibility(layer.name)}
            onDeleteLayer={() => onDeleteLayer(layer.name)}
            currentUserId={currentUserId}
            isDefaultLayer={layer.name === 'default'}
          />
        ))}
      </div>

      {/* Add layer */}
      <div className="p-3 border-t border-gray-800">
        {showNewLayerInput ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newLayerName}
              onChange={(e) => setNewLayerName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateLayer()}
              placeholder="Layer name..."
              autoFocus
              className="flex-1 bg-gray-800 text-white text-sm rounded px-3 py-1.5 border border-gray-700"
            />
            <button
              onClick={handleCreateLayer}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
            >
              Add
            </button>
            <button
              onClick={() => { setShowNewLayerInput(false); setNewLayerName(''); }}
              className="px-3 py-1.5 bg-gray-700 text-white text-sm rounded hover:bg-gray-600"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowNewLayerInput(true)}
            className="w-full flex items-center justify-center gap-2 py-2 text-sm text-blue-400 hover:bg-blue-500/10 rounded-lg"
          >
            <Plus className="w-4 h-4" />
            Add Layer
          </button>
        )}
      </div>
    </div>
  );
}
